// OpenRouter public model catalog. Fetched client-side; the endpoint
// is public (no auth) and CORS-friendly, so there's no reason to
// proxy through the backend. Cached for a day in sessionStorage so
// the room doesn't refetch on every mount.

import { useEffect, useState } from "react";

export interface OpenRouterModel {
  id: string;
  name: string;
  description?: string;
  context_length?: number;
  pricing?: { prompt?: string; completion?: string };
  /// Epoch seconds. OpenRouter exposes the model's public release date.
  created?: number;
  architecture?: {
    modality?: string;
    input_modalities?: string[];
    output_modalities?: string[];
    tokenizer?: string;
  };
}

// Typical conversational turn for cost weighting: ~1000 tokens of
// prompt (system + short history) and ~300 tokens of completion. The
// single dollar figure this produces lets the Captain rank models at
// a glance without mentally multiplying per-million rates.
const TYPICAL_PROMPT_TOKENS = 1000;
const TYPICAL_COMPLETION_TOKENS = 300;

/// Blended cost per typical turn. Returns "free" for $0 models and
/// null when pricing data is missing.
export function formatModelPrice(model: OpenRouterModel): string | null {
  const prompt = model.pricing?.prompt;
  const completion = model.pricing?.completion;
  if (prompt === undefined && completion === undefined) return null;
  const promptNum = prompt ? parseFloat(prompt) : 0;
  const completionNum = completion ? parseFloat(completion) : 0;
  if (!Number.isFinite(promptNum) || !Number.isFinite(completionNum)) return null;
  if (promptNum === 0 && completionNum === 0) return "free";
  const dollarsPerTurn =
    promptNum * TYPICAL_PROMPT_TOKENS + completionNum * TYPICAL_COMPLETION_TOKENS;
  if (dollarsPerTurn < 0.001) return `$${dollarsPerTurn.toFixed(5)}/turn`;
  if (dollarsPerTurn < 0.01) return `$${dollarsPerTurn.toFixed(4)}/turn`;
  if (dollarsPerTurn < 1) return `$${dollarsPerTurn.toFixed(3)}/turn`;
  return `$${dollarsPerTurn.toFixed(2)}/turn`;
}

/// Relative age only: "3d", "2w", "4mo", "1y".
export function formatModelAge(model: OpenRouterModel): string | null {
  if (!model.created) return null;
  const releasedMs = model.created * 1000;
  const ageMs = Date.now() - releasedMs;
  if (ageMs < 0) return null;
  const day = 24 * 60 * 60 * 1000;
  if (ageMs < 14 * day) {
    const days = Math.max(1, Math.round(ageMs / day));
    return `${days}d`;
  }
  if (ageMs < 60 * day) {
    const weeks = Math.round(ageMs / (7 * day));
    return `${weeks}w`;
  }
  if (ageMs < 365 * day) {
    const months = Math.round(ageMs / (30 * day));
    return `${months}mo`;
  }
  const years = Math.round(ageMs / (365 * day));
  return `${years}y`;
}

/// A short badge for non-standard modalities. Text-in / text-out is
/// the default and gets no badge — only models that accept or emit
/// something other than text surface a chip, so the Captain knows
/// not to @-mention an image-out or audio-only model for a chat reply.
export function formatModelModality(model: OpenRouterModel): string | null {
  const arch = model.architecture;
  if (!arch) return null;
  const inputs = (arch.input_modalities ?? []).map((s) => s.toLowerCase());
  const outputs = (arch.output_modalities ?? []).map((s) => s.toLowerCase());

  const nonTextOut = outputs.filter((m) => m !== "text");
  if (nonTextOut.length > 0) {
    const label = nonTextOut[0];
    if (label === "image") return "image-out";
    if (label === "audio") return "audio-out";
    if (label === "video") return "video-out";
    return `${label}-out`;
  }
  const nonTextIn = inputs.filter((m) => m !== "text");
  if (nonTextIn.length > 0) {
    const label = nonTextIn[0];
    if (label === "image") return "vision";
    if (label === "audio") return "audio-in";
    return `${label}-in`;
  }
  const modality = (arch.modality ?? "").toLowerCase();
  if (!modality || modality === "text->text") return null;
  if (modality.includes("image") && modality.endsWith("image")) return "image-out";
  if (modality.includes("image")) return "vision";
  return modality;
}

interface CatalogCache {
  fetched_at: number;
  models: OpenRouterModel[];
}

const CACHE_KEY = "wt_openrouter_models_v2";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function readCache(): CatalogCache | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CatalogCache;
    if (Date.now() - parsed.fetched_at > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(models: OpenRouterModel[]) {
  try {
    const payload: CatalogCache = { fetched_at: Date.now(), models };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // storage quota / private mode — cache miss next time, fine
  }
}

export async function fetchOpenRouterModels(): Promise<OpenRouterModel[]> {
  const cached = readCache();
  if (cached) return cached.models;

  const resp = await fetch("https://openrouter.ai/api/v1/models");
  if (!resp.ok) throw new Error(`OpenRouter /models failed: ${resp.status}`);
  const body = (await resp.json()) as { data?: OpenRouterModel[] };
  const models = Array.isArray(body.data) ? body.data : [];
  writeCache(models);
  return models;
}

export function useOpenRouterModels(): {
  models: OpenRouterModel[];
  loading: boolean;
  error: string | null;
} {
  const [models, setModels] = useState<OpenRouterModel[]>(() => readCache()?.models ?? []);
  const [loading, setLoading] = useState(models.length === 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await fetchOpenRouterModels();
        if (!cancelled) setModels(list);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Model list unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { models, loading, error };
}
